/**
 * One meaning per icon (docs/PATH-V2.md §4.3): board = lesson, puzzle = practice, die = mini-game, the bot's portrait
 * = game, arrows in a circle = review, cup = chapter checkpoint. Emoji where one exists; the lesson board is drawn.
 */
import { getPersona } from '@gambit/content';
import type { PathStepView, StepKind } from '@gambit/shared';
import { PersonaAvatar } from '../../ui/index.ts';

export const STEP_EMOJI: Record<StepKind, string> = {
  lesson: '♟️',
  practice: '🧩',
  minigame: '🎲',
  game: '♞',
  review: '🔁',
  checkpoint: '🏆',
};

/** what a step is, said to a screen reader (the visible caption is the step's own 1–3 words) */
export const STEP_KIND_RU: Record<StepKind, string> = {
  lesson: 'урок',
  practice: 'тренировка',
  minigame: 'мини-игра',
  game: 'партия',
  review: 'повторение',
  checkpoint: 'испытание',
};

/** A small chess board: the lesson's icon. */
function BoardGlyph({ size }: { size: number }) {
  const cells = [];
  for (let r = 0; r < 4; r++) for (let c = 0; c < 4; c++) if ((r + c) % 2 === 1) cells.push(<rect key={`${r}${c}`} x={2 + c * 5} y={2 + r * 5} width="5" height="5" />);
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="22" height="22" rx="3" fill="#f3ebd8" stroke="currentColor" strokeWidth="2" />
      <g fill="#5f9a86">{cells}</g>
    </svg>
  );
}

export function StepIcon({ step, size }: { step: Pick<PathStepView, 'kind' | 'persona'>; size: number }) {
  if (step.kind === 'lesson') return <BoardGlyph size={size} />;
  if (step.kind === 'game' && step.persona) {
    const persona = getPersona(step.persona);
    if (persona) return <PersonaAvatar persona={persona} size={size} mood="happy" label="" />;
  }
  return (
    <span aria-hidden="true" style={{ fontSize: Math.round(size * 0.82), lineHeight: 1 }}>
      {STEP_EMOJI[step.kind]}
    </span>
  );
}
